import { useCallback, useEffect, useState } from "react";
import { Spinner, Tooltip } from "@nextui-org/react";
import { FileText, Download } from "lucide-react";
import { apigetDownloadEnabledAffairs } from "../../services/apiCurrentAffairs/apicurrentaffairs";
import apiurl from "../../services/apiendpoint/apiendpoint";

export default function DownloadsPage() {
    const [loading, setLoading] = useState(false);
    const [downloadAffairs, setDownloadAffairs] = useState([]);
    const [selectedType, setSelectedType] = useState("");
    const [affairTypes, setAffairTypes] = useState([]);

    const fetchDownloadAffairs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apigetDownloadEnabledAffairs();
            if (res && res.data && res.data.length > 0) {
                setDownloadAffairs(res.data);
                const types = [...new Set(res.data.map(item => item.Current_Affair_Type))];
                setAffairTypes(types);
            } else {
                setDownloadAffairs([]);
                setAffairTypes([]);
            }
        } catch (error) {
            console.error("Error fetching download affairs:", error);
            setDownloadAffairs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            fetchDownloadAffairs();
        }
        return () => {
            isMounted = false;
        };
    }, [fetchDownloadAffairs]);

    const filteredAffairs = selectedType
        ? downloadAffairs.filter(affair => affair.Current_Affair_Type === selectedType)
        : downloadAffairs;

    const openPDF = (pdfPath) => {
        const pdfUrl = `${apiurl()}/${pdfPath}`;
        window.open(pdfUrl, '_blank');
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <>
            <div className="max-w-[85rem] mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Downloads</h1>
                    <p className="mt-2 text-lg text-gray-600">
                        Access PDF materials for Current Affairs
                    </p>
                </div>

                {affairTypes.length > 0 && (
                    <div className="mb-6">
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => setSelectedType("")}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                    selectedType === ""
                                        ? "bg-regal-blue text-white"
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                            >
                                All Types
                            </button>
                            {affairTypes.map((type, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedType(type)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        selectedType === type
                                            ? "bg-regal-blue text-white"
                                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                    }`}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <Spinner size="lg" color="primary" />
                    </div>
                ) : filteredAffairs.length === 0 ? (
                    <div className="p-8 text-center bg-gray-100 rounded-lg">
                        <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg text-gray-600">No downloads available</p>
                    </div>
                ) : (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {filteredAffairs.map((affair) => (
                            <div
                                key={affair._id}
                                className="p-6 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md"
                            >
                                <div className="mb-4">
                                    <h3 className="mb-2 text-xl font-semibold text-gray-900">
                                        {affair.Title}
                                    </h3>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-md">
                                            {affair.Current_Affair_Type}
                                        </span>
                                        {affair.Category && (
                                            <span className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-md">
                                                {affair.Category}
                                            </span>
                                        )}
                                    </div>
                                    {affair.Month && affair.Year && (
                                        <p className="text-sm text-gray-500">
                                            {affair.Month} {affair.Year}
                                        </p>
                                    )}
                                </div>

                                {affair.pdfFiles && affair.pdfFiles.length > 0 ? (
                                    <div className="space-y-2">
                                        <p className="mb-2 text-sm font-semibold text-gray-700">
                                            Available PDFs ({affair.pdfFiles.length}):
                                        </p>
                                        {affair.pdfFiles.map((pdf, index) => (
                                            <button
                                                key={pdf._id || index}
                                                onClick={() => openPDF(pdf.file)}
                                                className="flex items-center justify-between w-full p-3 transition-colors rounded-md bg-gray-50 hover:bg-blue-50 group"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-5 h-5 text-blue-600" />
                                                    <div className="text-left">
                                                        <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                                                            {pdf.title}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {pdf.originalName}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Download className="w-4 h-4 text-blue-600" />
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">No PDFs available</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* WhatsApp Button */}
            <a href="https://wa.me/9047127777?text=Hello%20I%20want%20to%20enquire%20about%20your%20services" target="_blank" rel="noopener noreferrer">
                <button>
                    <Tooltip color="success" content="Contact on Whatsapp Now!" placement="left-start" showArrow={true}>
                        <img src="/images/whatsapp.png" alt="" className="fixed z-50 bottom-24 right-5 w-14 h-14 animate-bounce-up-down" />
                    </Tooltip>
                </button>
            </a>

            {/* Scroll to Top Button */}
            <button
                id="to-top-button"
                title="Go To Top"
                className="fixed z-50 hidden p-4 text-lg font-semibold text-white transition-colors duration-300 border-0 rounded-full shadow-md bottom-5 right-5 w-14 h-14 bg-regal-blue hover:bg-regal-yellow"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                    <path d="M12 4l8 8h-6v8h-4v-8H4l8-8z" />
                </svg>
            </button>
        </>
    );
}